import React, { useState, useEffect } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  Button,
  Input,
  Select,
  SelectItem,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Table,
  TableHeader,
  TableBody,
  TableColumn,
  TableRow,
  TableCell,
  Chip,
  Accordion,
  AccordionItem,
} from "@heroui/react";
import { Plus, Trash, Warning } from "@phosphor-icons/react";
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { useSession } from '@clerk/clerk-react';
import { API_URL } from "constants/index";

interface Company {
  id: number;
  name: string;
  archived?: boolean;
}

interface ProductTemplate {
  id: number;
  name: string;
  company: Company;
}

interface BuildingAttributeTemplate {
  id: number;
  category: string;
  ledger: string;
  productTemplates: ProductTemplate[];
}

export const BuildingAttributeTemplateManager: React.FC = () => {
  const { session } = useSession();
  const [templates, setTemplates] = useState<BuildingAttributeTemplate[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [availableProductTemplates, setAvailableProductTemplates] = useState<ProductTemplate[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<number | ''>('');
  const [selectedProductTemplate, setSelectedProductTemplate] = useState<number | ''>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newTemplate, setNewTemplate] = useState({
    category: '',
    ledger: '',
  });

  useEffect(() => {
    Promise.all([
      fetchTemplates(),
      fetchCompanies(),
    ]).finally(() => setIsLoading(false));
  }, []);

  useEffect(() => {
    if (companies.length > 0) {
      fetchAllProductTemplates();
    }
  }, [companies]);

  const fetchTemplates = async () => {
    try {
      const response = await fetchWithAuth(`${API_URL}/clusters/templates`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      }, session);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch templates: ${response.statusText}`);
      }
      
      const data = await response.json();
      setTemplates(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching templates:', error);
      setError('Fout bij ophalen van templates');
      setTemplates([]);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await fetchWithAuth(`${API_URL}/companies`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      }, session);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch companies: ${response.statusText}`);
      }
      
      const data = await response.json();
      setCompanies(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching companies:', error);
      setError('Fout bij ophalen van bedrijven');
      setCompanies([]);
    }
  };

  const fetchAllProductTemplates = async () => {
    try {
      const allProductTemplates: ProductTemplate[] = [];
      
      // Fetch product templates for each company
      for (const company of companies) {
        try {
          const response = await fetchWithAuth(`${API_URL}/companies/${company.id}/product-templates`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json'
            }
          }, session);
          
          if (response.ok) {
            const companyTemplates = await response.json();
            if (Array.isArray(companyTemplates)) {
              // Ensure each template has the company reference
              const templatesWithCompany = companyTemplates.map(template => ({
                ...template,
                company: company
              }));
              allProductTemplates.push(...templatesWithCompany);
            }
          }
        } catch (error) {
          console.error(`Error fetching product templates for company ${company.id}:`, error);
        }
      }
      
      setAvailableProductTemplates(allProductTemplates);
    } catch (error) {
      console.error('Error fetching product templates:', error);
      setError('Fout bij ophalen van product templates');
      setAvailableProductTemplates([]);
    }
  };

  const handleCreateTemplate = async () => {
    try {
      const response = await fetchWithAuth(`${API_URL}/clusters/templates`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newTemplate)
      }, session);

      if (response.ok) {
        await fetchTemplates();
        setIsModalOpen(false);
        setNewTemplate({ category: '', ledger: '' });
      } else {
        throw new Error(`Failed to create template: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error creating template:', error);
      setError('Fout bij aanmaken van template');
    }
  };

  const handleDeleteTemplate = async (templateId: number) => {
    try {
      const response = await fetchWithAuth(`${API_URL}/clusters/templates/${templateId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        }
      }, session);
      
      if (response.ok) {
        await fetchTemplates();
      } else {
        throw new Error(`Failed to delete template: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      setError('Fout bij verwijderen van template');
    }
  };

  const handleAddProductTemplate = async (templateId: number, productTemplateId: number) => {
    try {
      const response = await fetchWithAuth(`${API_URL}/clusters/templates/${templateId}/product-templates/${productTemplateId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      }, session);
      
      if (response.ok) {
        await fetchTemplates();
      } else {
        throw new Error(`Failed to add product template: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error adding product template:', error);
      setError('Fout bij toevoegen van product template');
    }
  };

  const handleRemoveProductTemplate = async (templateId: number, productTemplateId: number) => {
    try {
      const response = await fetchWithAuth(`${API_URL}/clusters/templates/${templateId}/product-templates/${productTemplateId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        }
      }, session);
      
      if (response.ok) {
        await fetchTemplates();
      } else {
        throw new Error(`Failed to remove product template: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error removing product template:', error);
      setError('Fout bij verwijderen van product template');
    }
  };

  const getMissingCompanies = (template: BuildingAttributeTemplate) => {
    const connectedCompanyIds = template.productTemplates?.map(pt => pt.company.id) || [];
    return companies.filter(company => !company.archived && !connectedCompanyIds.includes(company.id));
  };

  const getAvailableProductTemplatesForCompany = (companyId: number) => {
    return availableProductTemplates.filter(pt => pt.company.id === companyId);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-neutral-secondary-mid">Templates laden...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-red-500">{error}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="space-y-1">
          <h2 className="text-xl font-semibold text-neutral-primary">Attribuut Templates</h2>
          <p className="text-sm text-neutral-secondary-mid">Beheer templates voor gebouw attributen</p>
        </div>
        <Button
          color="primary"
          startContent={<Plus />}
          onPress={() => setIsModalOpen(true)}
          className="bg-brick-primary text-brick-secondary-fine"
        >
          Template toevoegen
        </Button>
      </div>

      <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 mb-6">
        <div className="flex items-start gap-3">
          <div className="text-blue-600 mt-1">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div>
            <h3 className="text-sm font-medium text-blue-800 mb-1">Belangrijk</h3>
            <p className="text-sm text-blue-700">
              Na aanmaken moet je voor elk bedrijf een product template koppelen zodat de koppeling met Odoo kan worden gemaakt.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {templates.map((template) => (
          <Card key={template.id} className="bg-brick-secondary-fine">
            <CardHeader className="flex justify-between items-start">
              <div className="space-y-1">
                <h3 className="text-lg font-semibold text-neutral-primary">{template.category}</h3>
                <Chip color="secondary" variant="flat" className="bg-moss-primary text-brick-secondary-fine">
                  {template.ledger}
                </Chip>
              </div>
              <Button
                isIconOnly
                color="danger"
                variant="light"
                onPress={() => handleDeleteTemplate(template.id)}
              >
                <Trash />
              </Button>
            </CardHeader>
            <CardBody>
              <Accordion>
                <AccordionItem
                  key="product-templates"
                  aria-label="Product Templates"
                  title="Gekoppelde product templates"
                  subtitle={`${template.productTemplates?.length || 0} templates gekoppeld`}
                >
                  <div className="space-y-4">
                    {template.productTemplates && template.productTemplates.length > 0 && (
                      <Table aria-label="Product Templates">
                        <TableHeader>
                          <TableColumn>Naam</TableColumn>
                          <TableColumn>Bedrijf</TableColumn>
                          <TableColumn>Acties</TableColumn>
                        </TableHeader>
                        <TableBody>
                          {template.productTemplates.map((pt) => (
                            <TableRow key={pt.id}>
                              <TableCell>{pt.name}</TableCell>
                              <TableCell>{pt.company?.name || 'Onbekend'}</TableCell>
                              <TableCell>
                                <Button
                                  isIconOnly
                                  color="danger"
                                  variant="light"
                                  onPress={() => handleRemoveProductTemplate(template.id, pt.id)}
                                >
                                  <Trash />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}

                    <div className="space-y-3">
                      <div className="flex gap-2">
                        <Select
                          label="Bedrijf"
                          placeholder="Selecteer een bedrijf"
                          value={selectedCompany}
                          onChange={(e) => {
                            const companyId = Number(e.target.value);
                            setSelectedCompany(companyId);
                            setSelectedProductTemplate(''); // Reset product template when company changes
                          }}
                          className="flex-1"
                        >
                          {companies.filter(company => !company.archived).map((company) => (
                            <SelectItem key={company.id}>
                              {company.name}
                            </SelectItem>
                          ))}
                        </Select>
                      </div>
                      
                      {selectedCompany && (
                        <div className="flex gap-2">
                          <Select
                            label="Product Template"
                            placeholder="Selecteer een product template"
                            value={selectedProductTemplate}
                            onChange={(e) => setSelectedProductTemplate(Number(e.target.value))}
                            className="flex-1"
                          >
                            {getAvailableProductTemplatesForCompany(selectedCompany as number).map((productTemplate) => (
                              <SelectItem key={productTemplate.id}>
                                {productTemplate.name}
                              </SelectItem>
                            ))}
                          </Select>
                          <Button
                            color="primary"
                            onPress={() => {
                              if (selectedCompany && selectedProductTemplate) {
                                handleAddProductTemplate(template.id, selectedProductTemplate as number);
                                setSelectedCompany('');
                                setSelectedProductTemplate('');
                              }
                            }}
                            className="bg-brick-primary text-brick-secondary-fine"
                            isDisabled={!selectedCompany || !selectedProductTemplate}
                          >
                            Toevoegen
                          </Button>
                        </div>
                      )}
                      
                      {selectedCompany && getAvailableProductTemplatesForCompany(selectedCompany as number).length === 0 && (
                        <p className="text-sm text-neutral-secondary-mid">
                          Geen beschikbare product templates voor dit bedrijf.
                        </p>
                      )}
                    </div>
                  </div>
                </AccordionItem>
              </Accordion>

              {getMissingCompanies(template).length > 0 && (
                <div className="mt-4 p-4 bg-warning-50 rounded-lg">
                  <div className="flex items-center text-warning mb-2">
                    <Warning className="mr-2" />
                    <span className="font-semibold">Ontbrekende product templates voor:</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {getMissingCompanies(template).map((company) => (
                      <Chip
                        key={company.id}
                        color="warning"
                        variant="flat"
                      >
                        {company.name}
                      </Chip>
                    ))}
                  </div>
                </div>
              )}
            </CardBody>
          </Card>
        ))}
      </div>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <ModalContent>
          <ModalHeader>Nieuwe template toevoegen</ModalHeader>
          <ModalBody>
            <div className="space-y-4">
              <Input
                label="Categorie"
                placeholder="Bijv. Buitenstaanplaats, Overdekte parkeerplaats, Dienst, etc."
                value={newTemplate.category}
                onChange={(e) => setNewTemplate({ ...newTemplate, category: e.target.value })}
                isRequired
              />
              <Input
                label="Grootboek"
                placeholder="Bijv. 4000, 4100, etc."
                value={newTemplate.ledger}
                onChange={(e) => setNewTemplate({ ...newTemplate, ledger: e.target.value })}
              />
            </div>
          </ModalBody>
          <ModalFooter>
                          <Button 
                color="danger" 
                variant="light" 
                onPress={() => {
                  setIsModalOpen(false);
                  setNewTemplate({ category: '', ledger: '' });
                }}
              >
              Annuleren
            </Button>
            <Button 
              color="primary" 
              onPress={handleCreateTemplate}
              className="bg-brick-primary text-brick-secondary-fine"
            >
              Toevoegen
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
}; 